import React, { useCallback, useEffect, useState } from 'react';
import withTranslation from 'hoc/withTranslation';
import styled from '@emotion/styled';
import { setTourParameter } from 'actions';
import { useDispatch, useSelector } from 'react-redux';
import ObjectiveFunctionsList from './ObjectiveFunctionsList';
import { enableObjectiveFunction } from '../../../utils/solverConfig/config';

const StyledObjectiveFunctionsContainer = styled.div({
  padding: '1.2rem 0.5rem 0.5rem 0.5rem',
});

const StyledCheckboxWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1rem',
});

const HiddenCheckbox = styled.input({
  position: 'absolute',
  opacity: 0,
  cursor: 'pointer',
  height: 0,
  width: 0,
});

const StyledCheckboxVisible = styled.div(({ checked }) => ({
  width: '14px',
  height: '14px',
  backgroundColor: '#fff',
  border: '1.5px solid #373d45',
  borderRadius: '2px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    borderColor: '#4d89f5',
  },
  '&::after': checked
    ? {
        content: '"✓"',
        color: '#000',
        fontSize: '11px',
        fontWeight: 'bold',
        lineHeight: '1',
      }
    : {},
}));

const StyledLabel = styled.label(({ checked }) => ({
  color: '#fff',
  fontSize: '14px',
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontWeight: checked ? '500' : '200',
}));

const ObjectiveFunctions = ({
  objectiveFunctions,
  onChange,
  terminationCriteriaEnabled,
  solution,
  translations: {
    solverConfigLabels: { objectiveFunctionsLabels },
  },
}) => {
  const dispatch = useDispatch();

  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter({ ...parameter, index: solution.show })),
    [dispatch, solution.show],
  );
  const [objectiveFunctionsEnabled, setObjectiveFunctionsEnabled] = useState(
    objectiveFunctions.enabled,
  );
  const [balanceObjectivesEnabled, setBalanceObjectivesEnabled] = useState(
    objectiveFunctions.values?.some((item) => item.enabled && item.balance),
  );
  const [calculatedHeight, setCalculatedHeight] = useState('');

  const handleEnabledValueChange = useCallback(
    (val) => {
      if (!onChange) return;

      const toReturn = { enabled: val, values: objectiveFunctions.values };
      handleSetTourParameter({
        solverConfiguration: {
          objectiveFunctions: toReturn,
          terminationCriteria: tourPlanner.solverConfiguration.terminationCriteria,
        },
      });
      onChange(toReturn);
    },
    [onChange, objectiveFunctions, tourPlanner, handleSetTourParameter],
  );

  const handleOnBalanceSwitchChange = useCallback((val) => {
    setBalanceObjectivesEnabled(val);

    if (val === false && objectiveFunctions.values) {
      const enabledBalance = objectiveFunctions.values.findIndex(
        (item) => item.balance && item.enabled,
      );
      if (enabledBalance !== -1) {
        const newVal = enableObjectiveFunction(objectiveFunctions.values, enabledBalance);
        onChange({ enabled: objectiveFunctionsEnabled, values: newVal });
      }
    }
  });

  const handleOnSwitchChange = useCallback(
    (e) => {
      const val = e.target.checked;
      setObjectiveFunctionsEnabled(val);
      handleOnBalanceSwitchChange(false);
      handleEnabledValueChange(val);
    },
    [handleOnBalanceSwitchChange, handleEnabledValueChange],
  );

  const handleOnChangeObjectiveFunctions = useCallback(
    (newObjectiveFunctions) => {
      if (!onChange) return;

      const toReturn = { enabled: objectiveFunctionsEnabled, values: newObjectiveFunctions };

      handleSetTourParameter({
        solverConfiguration: {
          objectiveFunctions: toReturn,
          terminationCriteria: tourPlanner.solverConfiguration.terminationCriteria,
        },
      });
      onChange(toReturn);
    },
    [
      onChange,
      objectiveFunctionsEnabled,
      handleSetTourParameter,
      tourPlanner.solverConfiguration.terminationCriteria,
    ],
  );

  const checkboxLabel = objectiveFunctionsLabels.switchLabel;

  useEffect(() => {
    let height = 28;
    if (terminationCriteriaEnabled) {
      if (balanceObjectivesEnabled) height = 43;
      else height = 34;
    } else if (balanceObjectivesEnabled) {
      height = 40;
    }
    setCalculatedHeight(`calc(100vh - ${height}rem)`);
  }, [balanceObjectivesEnabled, objectiveFunctionsEnabled, terminationCriteriaEnabled]);

  useEffect(() => {
    setObjectiveFunctionsEnabled(objectiveFunctions.enabled);
  }, [objectiveFunctions]);

  return (
    <StyledObjectiveFunctionsContainer data-testid="objectiveEnablerToggle">
      <StyledCheckboxWrapper>
        <StyledLabel htmlFor="objective-functions-enabler" checked={objectiveFunctionsEnabled}>
          <StyledCheckboxVisible checked={objectiveFunctionsEnabled} />
          <HiddenCheckbox
            type="checkbox"
            id="objective-functions-enabler"
            checked={objectiveFunctionsEnabled}
            onChange={handleOnSwitchChange}
          />
          {checkboxLabel}
        </StyledLabel>
      </StyledCheckboxWrapper>
      {objectiveFunctionsEnabled && (
        <ObjectiveFunctionsList
          objectiveFunctions={objectiveFunctions.values}
          onChange={handleOnChangeObjectiveFunctions}
          height={calculatedHeight}
          isBalanceObjective={false}
        />
      )}
    </StyledObjectiveFunctionsContainer>
  );
};

export default withTranslation(ObjectiveFunctions);
